import { Button, Alert, FormField, TextInput } from "@a2z/ui";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useLoginMutation } from "@/modules/auth/services/useLoginMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";

export function LoginForm({ locale }: { locale: string }) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: login } = useLoginMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["email", "password"],
      t(keys.errors.required)
    );

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await login({
        email: String(formData.get("email") ?? ""),
        password: String(formData.get("password") ?? ""),
      });
      navigate(`/${locale}/admin`, { replace: true });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="mx-auto flex w-full max-w-[640px] flex-col gap-4" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      <FormField
        label={t(keys.common.email)}
        name="email"
        required
        error={fieldErrors.email}
      >
        <TextInput
          name="email"
          type="email"
          autoComplete="email"
          placeholder={t(keys.placeholders.email)}
          required
          hasError={Boolean(fieldErrors.email)}
        />
      </FormField>
      <FormField
        label={t(keys.common.password)}
        name="password"
        helperText={t(keys.auth.passwordHint)}
        required
        error={fieldErrors.password}
      >
        <TextInput
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder={t(keys.placeholders.password)}
          required
          hasError={Boolean(fieldErrors.password)}
        />
      </FormField>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary" className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition active:translate-y-px disabled:opacity-60"
      >
        {t(keys.auth.loginAction)}
      </Button>
      <p className="text-xs text-[color:var(--text-muted)]">
        {t(keys.auth.adminNotice)}
      </p>
    </form>
  );
}
